
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import vSelect3 from "vselect3";
import { debounce, unionWith } from 'lodash';
import axios, { CancelTokenSource } from "axios";
import { ServerTypeFilterOption, ServerTypeEventFilterOption } from '@/store/reports/types';
import api from '@/services/api.service';
import isEqual from 'lodash.isequal';
import { getDebouncePeriod } from '@/utils/debounce-helper';

@Component({
    components: {
        "v-select-3": vSelect3
    }
})
export default class EventTypeReportFilter extends Vue {
    @Prop()
    public value: ServerTypeEventFilterOption[];

    private readonly debouncePeriod: number = getDebouncePeriod();
    private readonly maxServerTypeResultSize: number = 100;
    private readonly maxServerTypeEventsResultSize: number = 100;
    private readonly pageNumber: number = 1;

    private readonly noServerTypesFound: string = 'No device types were found';
	private readonly noServerTypeEventsFound: string = 'No event types were found';
    private readonly noServerTypeSelectedMessage: string = 'You must select a device type';
    private readonly maxServerTypeMessage: string = `Showing a maximum of ${this.maxServerTypeResultSize} device types, if you cannot find the device type you are looking for - try refining your search`;
    private readonly maxServerTypeEventMessage: string = `Showing a maximum of ${this.maxServerTypeEventsResultSize} event types, if you cannot find the event type you are looking for - try refining your search`;

    private serverTypeSearchTerm: string = "";
    private serverTypeEventsSearchTerm: string = "";
    private serverTypeSearchToken: CancelTokenSource | null = null;
    private serverTypeEventsSearchToken: CancelTokenSource | null = null;
    private serverTypes: ServerTypeFilterOption[] = [];
    private serverTypeEvents: ServerTypeEventFilterOption[] = [];

    private selectedServerType: ServerTypeFilterOption = null;
    private selectedServerTypeEvents: ServerTypeEventFilterOption[] = [];

    private mounted(): void {
        if (this.value)
        {
            this.serverTypeEvents = this.value;
            this.selectedServerTypeEvents = this.value;
        }

        this.performServerTypeSearch(this.serverTypeSearchTerm);
    }

    private async handleServerTypeSearchInput(searchTerm: string): Promise<void> {
        this.serverTypeSearchTerm = searchTerm;
        await this.performServerTypeSearch(searchTerm);
    }

    private performServerTypeSearch: (searchTerm: string) => Promise<void> = debounce(
        async function (searchTerm: string) {
            if (this.serverTypeSearchToken) {
                this.serverTypeSearchToken.cancel("Cancel search");
            }

            this.serverTypeSearchToken = axios.CancelToken.source();

            try {
                this.serverTypes = await api.getServerTypeFilterOptions(
                    this.pageNumber, this.maxServerTypeResultSize, this.serverTypeSearchTerm);
			} catch (e) {
				return; //Return when past requests are cancelled
			}
        },
        this.debouncePeriod,
        { leading: false, trailing: true }
    );

    private async handleServerTypeEventsSearchInput(searchTerm: string): Promise<void> {
        this.serverTypeEventsSearchTerm = searchTerm
        await this.performServerTypeEventsSearch(searchTerm);
    }

    private performServerTypeEventsSearch: (searchTerm: string) => Promise<void> = debounce(
        async function (searchTerm: string) {
            if (this.serverTypeEventsSearchToken) {
                this.serverTypeEventsSearchToken.cancel("Cancel search");
            }

            this.serverTypeEventsSearchToken = axios.CancelToken.source();

            try {
				let options = await api.getServerTypeEventsFilterOptions(
                    this.pageNumber, 
                    this.maxServerTypeEventsResultSize, 
                    this.serverTypeEventsSearchTerm,
                    [this.selectedServerType.serverTypeId]);
                this.serverTypeEvents = unionWith(options, this.value, isEqual);
			} catch (e) {
				return; //Return when past requests are cancelled
			}
        },
        this.debouncePeriod,
        { leading: false, trailing: true }
    )

    @Watch("selectedServerType")
    public async serverTypeChanged(newValue: ServerTypeFilterOption): Promise<void> {
        this.serverTypeEventsSearchTerm = "";
        this.serverTypeEvents = [];

        if (newValue != null) {
            await this.performServerTypeEventsSearch(this.serverTypeEventsSearchTerm);
        }
        else if (this.value) {
            this.serverTypeEvents = this.value;
        }
    }

    @Watch("selectedServerTypeEvents")
    public async serverTypeEventChanged(): Promise<void> {
        this.$emit("input", this.selectedServerTypeEvents);
    }
}
